import { Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';

import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';
import { JobOffering, JobPeriod } from 'types/AppTypes';
import { isJobPeriodActive } from 'utils/jobPeriodHelper';

import JobOfferPeriod from '../../JobOfferDetail/components/JobOfferPeriod';

const SelectJobPeriods = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<{
    jobOffering: JobOffering & { jobPeriods: JobPeriod[] };
    jobPeriodIds: number[];
  }>();

  const handleClickPeriod = id => {
    if (!!values?.jobPeriodIds?.find(jpI => jpI === id)) {
      setFieldValue(
        'jobPeriodIds',
        values.jobPeriodIds.filter(jpi => jpi !== id)
      );
    } else {
      setFieldValue('jobPeriodIds', [...values.jobPeriodIds, id]);
    }
  };

  // ? Show info that no jobOffering is selected
  if (values.jobOffering === null) {
    return (
      <Typography variant="h6">
        {t(translations.pages.deactivateJobOffers.noJobOfferSelected())}
      </Typography>
    );
  }

  const activeJobPeriod = values.jobOffering.jobPeriods.find(isJobPeriodActive);
  const inactiveJobPeriods = values.jobOffering.jobPeriods.filter(
    jp => jp.id !== activeJobPeriod?.id
  );

  if (!activeJobPeriod && !inactiveJobPeriods.length) {
    return (
      <Typography variant="h6">
        {t(translations.pages.deactivateJobOffers.noPeriodsAvailable())}
      </Typography>
    );
  }

  return (
    <Grid item xs={12}>
      <Grid container spacing={2} justifyContent={'flex-end'}>
        <Grid item>
          <SelectAllSwitch />
        </Grid>
        {activeJobPeriod && (
          <>
            <Grid item xs={12}>
              <Typography component={'h2'}>
                {t(translations.pages.deactivateJobOffers.jobPeriods.activeJobPeriod())}
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} container spacing={2}>
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                xl={3}
                onClick={() => handleClickPeriod(activeJobPeriod.id)}
              >
                <JobOfferPeriod
                  key={activeJobPeriod.id}
                  selected={!!values.jobPeriodIds.find(jpi => jpi === activeJobPeriod.id)}
                  period={activeJobPeriod}
                  readonly
                  companyId={0}
                  creatorId={0}
                  user={0}
                />
              </Grid>
            </Grid>
          </>
        )}
        {inactiveJobPeriods.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography component={'h2'}>
                {t(translations.pages.deactivateJobOffers.jobPeriods.inactiveJobPeriod())}
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12} container spacing={2}>
              {inactiveJobPeriods.map(jp => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  xl={3}
                  onClick={() => handleClickPeriod(jp.id)}
                  key={`${jp.id}__select_g`}
                >
                  <JobOfferPeriod
                    key={`${jp.id}__select`}
                    selected={!!values.jobPeriodIds.find(jpi => jpi === jp.id)}
                    period={jp}
                    readonly
                    companyId={0}
                    creatorId={0}
                    user={0}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const SelectAllSwitch = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<{
    jobOffering: JobOffering & { jobPeriods: JobPeriod[] };
    jobPeriodIds: number[];
  }>();

  const handleChange = checked => {
    if (checked) {
      setFieldValue('jobPeriodIds', [...values.jobOffering.jobPeriods.map(jp => jp.id)]);
    } else {
      setFieldValue('jobPeriodIds', []);
    }
  };

  return (
    <FormControlLabel
      checked={values.jobPeriodIds.length === values.jobOffering.jobPeriods.length}
      control={<Switch onChange={event => handleChange(event.target.checked)} />}
      label={t(translations.pages.deactivateJobOffers.jobPeriods.selectAll())}
    />
  );
};

export default SelectJobPeriods;
